---
title: "The AI scheduling email assistant"
description: "The AI email assistant in Chosen reads candidate replies on scheduling threads, classifies the intent, and drafts responses to reschedules and cancellations."
category: "scheduling"
keywords:
  - "AI email assistant"
  - "scheduling email automation"
  - "candidate reply handling"
  - "reschedule interview email"
schema: "HowTo"
nav:
  section: "guides"
  order: 30
  parent: "/guides/scheduling"
---

When a candidate replies to a scheduling email — "can we do Tuesday instead?", "I need to push this back a week" — the AI email assistant reads it, works out what they want, and drafts a response. You see the draft before it sends. Most of the time you just approve it. It needs [Gmail](/getting-started/integrations/gmail) connected.

## What it does with a reply

Every reply on a scheduling thread gets read and sorted into an intent — reschedule, cancel, a logistics question, a plain confirmation, or unclear — with a confidence score attached. The score is the assistant's own read on how sure it is. A high-confidence reschedule gets a clean drafted reply; a low-confidence or unclear one is flagged for you to look at rather than answered automatically.

It handles the common cases:

- **Reschedules** — the candidate's, and yours. It finds new slots and offers them.
- **"Can we add my colleague to this interview?"** — it folds the extra interviewer in.
- **Cancellations** — it cancels the meeting and acknowledges.
- **Confirmations** — a candidate saying "Tuesday works" needs no drafting; it moves things forward.

Drafted replies wait in the [AI events tab](/guides/scheduling/ai-events-tab) for you to approve, edit, or reject.

## It only touches threads Chosen started

The assistant acts on scheduling threads that began inside Chosen. It does not read or reply to the rest of your inbox — your other email is not its business. If a scheduling conversation started somewhere else, the assistant isn't in it.

## The monitoring window

The assistant watches a scheduling thread for about 14 days, plus a buffer after the meeting, then stops. That covers the realistic life of a scheduling conversation. A reply that lands months later, on a long-dead thread, won't wake it back up — start a fresh scheduling request instead.

<Callout type="warning" title="Don't reply 'thanks!' on a thread you want the AI to keep handling">
A reply from you on a scheduling thread is the signal that you're taking over — the assistant backs off and stops drafting. If you just want to leave a note without sending an email and without stopping the AI, use the **Comment** action on the thread inside Chosen. That adds an internal note and keeps the assistant in the loop.
</Callout>

## When it gets it wrong

- **It misread the reply.** The candidate said "Tuesday morning" and the assistant offered the afternoon. Open the thread, send a one-line correction yourself, and it picks up from there — your reply tells it you've got this one.
- **Low confidence, no draft.** The reply was ambiguous, so the assistant flagged it in the [AI events tab](/guides/scheduling/ai-events-tab) instead of answering. That's working as intended — it asks rather than guesses.
- **The tone is off.** Drafted replies follow your scheduling email templates and personality setting under **Settings → Scheduling**. Adjust those and the drafts follow.

## Limits

- No [Gmail](/getting-started/integrations/gmail) connection means no assistant — it has nothing to read.
- It won't negotiate forever. After a few reschedule rounds it stops and hands the thread to you.
- It reads scheduling-shaped messages, not free-form requests buried in long email chains. Keep scheduling replies on the scheduling thread.
